import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, SVGOverlay, useMapEvents } from 'react-leaflet';
import { createPathComponent } from '@react-leaflet/core';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import NodeMarker from './icon/gswm-marker.svg';
import NodeMarkerOnline from './icon/gswm-marker-online.svg';
import NodeMarkerOffline from './icon/gswm-marker-offline.svg';
import NodeMarkerNotUse from './icon/gswm-marker-not-use.svg';
import NodeMarkerAlarm from './icon/gswm-marker-alarm.svg';

import { Button, Col, Row, Select, Switch, Typography } from 'antd';
import { ReactComponent as LocationSvg } from './icon/Location.svg';
import { useNavigate } from 'react-router-dom';
import { Input } from 'antd'; // Use Ant Design's Input for search
import {
  RightOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';

import _ from 'lodash';
import { formatCurrency } from '@utils/utils';

// ─── Marker Cluster Group (react-leaflet v4 compatible) ───────────────────────
const createClusterGroup = (props, context) => {
  const { children: _c, ...options } = props;
  const clusterGroup = new L.MarkerClusterGroup(options);
  return { instance: clusterGroup, context: { ...context, layerContainer: clusterGroup } };
};
const MarkerClusterGroup = createPathComponent(createClusterGroup);

const getDefaultClusterColor = (count) => {
  if (count < 10) {
    return { color: '#52c41a', size: 36 };
  }
  if (count < 50) {
    return { color: '#faad14', size: 42 };
  }
  return { color: '#0051a9', size: 50 };
};

const getFilteredClusterColor = ({ count, deviceStatus, installationStatus, warningStatus }) => {
  const defaultConfig = getDefaultClusterColor(count);

  if (warningStatus === 'with_warning') {
    return { ...defaultConfig, color: '#faad14' };
  }

  if (deviceStatus === 'online') {
    return { ...defaultConfig, color: '#52c41a' };
  }

  if (deviceStatus === 'offline') {
    return { ...defaultConfig, color: '#ff4d4f' };
  }

  if (installationStatus === 'not_installed') {
    return { ...defaultConfig, color: '#8c8c8c' };
  }

  if (installationStatus === 'installed') {
    return { ...defaultConfig, color: '#0051a9' };
  }

  return defaultConfig;
};

const createClusterIcon =
  ({ deviceStatus, installationStatus, warningStatus }) =>
  (cluster) => {
    const count = cluster.getChildCount();
    const { color, size } = getFilteredClusterColor({
      count,
      deviceStatus,
      installationStatus,
      warningStatus
    });

    return L.divIcon({
      html: `<div style="
      width:${size}px;height:${size}px;
      background:${color};
      border:3px solid #fff;
      border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      box-shadow:0 2px 8px rgba(0,0,0,0.35);
      font-weight:700;font-size:${count >= 100 ? 11 : 13}px;color:#fff;
    ">${count}</div>`,
      className: '',
      iconSize: L.point(size, size),
      iconAnchor: L.point(size / 2, size / 2)
    });
  };
// ──────────────────────────────────────────────────────────────────────────────

const customIcon = new L.Icon({
  iconUrl: NodeMarker,
  iconSize: [20, 20],
  iconAnchor: [8, 8],
  popupAnchor: [0, -45]
});

const boundHoangSa = [
  [18.3, 110.1],
  [15.2, 113.7]
];

const boundTruongSa = [
  [10.3, 110.7],
  [8.3, 113.7]
];

const OpenLayersMap = ({
  markers,
  onSearch,
  onFilterChange,
  onOpenAdvanced,
  deviceStatus,
  installationStatus,
  warningStatus
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [isSatellite, setIsSatellite] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(14);
  const isInitialSearch = React.useRef(true);
  const onSearchRef = React.useRef(onSearch);
  const searchTimeoutRef = React.useRef(null);
  onSearchRef.current = onSearch;

  React.useEffect(() => {
    if (isInitialSearch.current) {
      isInitialSearch.current = false;
      return undefined;
    }

    searchTimeoutRef.current = setTimeout(() => {
      onSearchRef.current(search);
    }, 1000);

    return () => clearTimeout(searchTimeoutRef.current);
  }, [search]);

  const createDynamicIcon = (zoom, marker) => {
    const baseWidth = 34;
    const scaleFactor = Math.max(0.85, Math.min(1.35, zoom / 12));
    const width = baseWidth * scaleFactor;
    const height = width * (48 / 40);

    // Xác định icon dựa trên trạng thái
    let iconUrl = NodeMarkerNotUse; // Default cho not_use
    let hasAlarm = false;

    // Kiểm tra alarm_status trước (ưu tiên cao nhất)
    if (!_.isEmpty(marker?.alarm_status)) {
      iconUrl = NodeMarkerAlarm;
      hasAlarm = true;
    }
    // Sau đó kiểm tra status
    else if (marker?.status === 'online') {
      iconUrl = NodeMarkerOnline;
    } else if (marker?.status === 'offline') {
      iconUrl = NodeMarkerOffline;
    }

    const haloSize = width + 10;

    return L.divIcon({
      html: `<div style="
        position:relative;
        width:${haloSize}px;
        height:${height + 10}px;
        display:flex;
        align-items:flex-start;
        justify-content:center;
      ">
        ${
          hasAlarm
            ? `<span style="
                position:absolute;
                top:3px;
                width:${width}px;
                height:${width}px;
                border-radius:50%;
                background:rgba(250, 173, 20, 0.32);
                box-shadow:0 0 0 5px rgba(250, 173, 20, 0.15);
              "></span>`
            : ''
        }
        <img src="${iconUrl}" alt="" style="
          position:relative;
          width:${width}px;
          height:${height}px;
          filter:drop-shadow(0 4px 5px rgba(0, 0, 0, 0.35));
        " />
      </div>`,
      className: '',
      iconSize: [haloSize, height + 10],
      iconAnchor: [haloSize / 2, height],
      popupAnchor: [0, -height]
    });
  };

  const MapEvents = () => {
    const map = useMapEvents({
      zoomend: () => {
        setCurrentZoom(map.getZoom());
      }
    });
    return null;
  };

  const handleViewChange = (checked) => {
    setIsSatellite(checked);
  };

  const handleDirectionsClick = (lat, lng) => {
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(googleMapsUrl, '_blank');
  };

  return (
    <div
      style={{
        position: 'relative',
        zIndex: 0,
        width: '100%',
        height: 'calc(100vh - 90px)', // Adjust height to fit within the layout
        overflow: 'hidden'
      }}>
      {/* Search Input overlay */}
      <div
        style={{
          position: 'absolute',
          top: '10px',
          right: '50px',
          zIndex: 1000,
          width: 'min(920px, calc(100% - 70px))',
          padding: 10,
          // background: 'rgba(181, 188, 255, 0.95)',
          borderRadius: 8,
          boxShadow: '0 2px 10px rgba(4, 20, 44, 0.15)'
        }}>
        <Row gutter={[8, 8]} align="middle">
          <Col xs={24} md={12} lg={7}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm kiếm mã, tên khách hàng..."
              prefix={<SearchOutlined />}
              style={{ height: 36 }}
            />
          </Col>
          <Col xs={12} md={6} lg={4}>
            <Select
              allowClear
              placeholder="Online / Offline"
              value={deviceStatus}
              style={{ width: '100%' }}
              options={[
                { label: 'Online', value: 'online' },
                { label: 'Offline', value: 'offline' }
              ]}
              onChange={(value) => onFilterChange('deviceStatus', value)}
            />
          </Col>
          <Col xs={12} md={6} lg={5}>
            <Select
              allowClear
              placeholder="Trạng thái lắp đặt"
              value={installationStatus}
              style={{ width: '100%' }}
              options={[
                { label: 'Đã lắp đặt', value: 'installed' },
                { label: 'Chưa lắp đặt', value: 'not_installed' }
              ]}
              onChange={(value) => onFilterChange('installationStatus', value)}
            />
          </Col>
          <Col xs={12} md={6} lg={4}>
            <Select
              allowClear
              placeholder="Cảnh báo"
              value={warningStatus}
              style={{ width: '100%' }}
              options={[
                { label: 'Có cảnh báo', value: 'with_warning' },
                { label: 'Không cảnh báo', value: 'without_warning' }
              ]}
              onChange={(value) => onFilterChange('warningStatus', value)}
            />
          </Col>
          <Col xs={12} md={6} lg={4}>
            <Button
              icon={<FilterOutlined />}
              onClick={onOpenAdvanced}
              style={{ width: '100%', height: 36 }}>
              Tìm kiếm nâng cao
            </Button>
          </Col>
        </Row>
      </div>
      {/* Switch between standard and satellite views */}
      <div style={{ position: 'absolute', bottom: '50px', left: '10px', zIndex: 1000 }}>
        <Switch checked={isSatellite} onChange={handleViewChange} size="large" />
        <div style={{ color: '#728ee4', fontWeight: '500', marginTop: '8px', fontSize: '16px' }}>
          Xem vệ tinh
        </div>
      </div>

      {/* MapContainer with Leaflet map */}
      <MapContainer
        center={[20.88721682785363, 106.59237019649302]}
        zoom={14}
        style={{ width: '100%', height: '100%', borderRadius: 0 }}>
        {/* Standard or Satellite TileLayer based on switch */}
        {isSatellite ? (
          <TileLayer
            url={`https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=pk.eyJ1Ijoia2hhbmh0diIsImEiOiJjbGxuOTg4MmMwNmJpM2RrcXJiemZxZ2RoIn0.n9pf4W2tjvhg7mT5QZLggA`}
            attribution='Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
        ) : (
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        )}
        <SVGOverlay attributes={{ stroke: 'none' }} bounds={boundHoangSa}>
          <ellipse
            cx="100%"
            cy="100%"
            rx="100%"
            ry="100%"
            fill="#aad3df"
            fillOpacity="1"
            stroke="none"
          />
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#222"
            stroke="#fff"
            strokeWidth="2"
            paintOrder="stroke"
            style={{
              fontSize: `${Math.max(10, Math.min(30, currentZoom * 1.5))}px`,
              fontWeight: '700',
              fontFamily: 'Arial, sans-serif',
              letterSpacing: '0.5px'
            }}>
            Hoàng Sa (Việt Nam)
          </text>
        </SVGOverlay>
        <SVGOverlay attributes={{ stroke: 'red' }} bounds={boundTruongSa}>
          <ellipse
            cx="100%"
            cy="100%"
            rx="100%"
            ry="100%"
            fill="#aad3df"
            fillOpacity="1"
            stroke="none"
          />
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#222"
            stroke="#fff"
            strokeWidth="2"
            paintOrder="stroke"
            style={{
              fontSize: `${Math.max(10, Math.min(30, currentZoom * 1.5))}px`,
              fontWeight: '700',
              fontFamily: 'Arial, sans-serif',
              letterSpacing: '0.5px'
            }}>
            {'Trường Sa(Việt Nam)'}
          </text>
        </SVGOverlay>
        <MapEvents />
        <MarkerClusterGroup
          key={`${deviceStatus || 'all'}-${installationStatus || 'all'}-${warningStatus || 'all'}`}
          iconCreateFunction={createClusterIcon({
            deviceStatus,
            installationStatus,
            warningStatus
          })}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
          zoomToBoundsOnClick={true}
          maxClusterRadius={60}
          spiderfyDistanceMultiplier={2}
          chunkedLoading={true}>
          {markers?.map((marker) => (
            <Marker
              position={[marker.lat, marker.lng]}
              key={marker.id}
              icon={createDynamicIcon(currentZoom, marker)}>
              <Popup>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <Typography.Text style={{ fontSize: '16px', fontWeight: '500' }}>
                    {marker?.code} - {marker?.name}
                  </Typography.Text>
                  {/* Online/Offline Status */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {marker?.status === 'online' ? (
                      <CheckCircleOutlined style={{ fontSize: '20px', color: '#52c41a' }} />
                    ) : (
                      <CloseCircleOutlined style={{ fontSize: '20px', color: '#ff4d4f' }} />
                    )}
                    <Typography.Text
                      style={{
                        fontSize: '14px',
                        color: marker?.status === 'online' ? '#52c41a' : '#ff4d4f'
                      }}>
                      {marker?.status === 'online'
                        ? 'Online'
                        : marker?.status === 'offline'
                        ? 'Offline'
                        : 'Chưa lắp đặt'}
                    </Typography.Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <LocationSvg style={{ marginRight: '4px' }} />
                    <Typography.Text style={{ fontSize: '14px', color: '#52525B' }}>
                      {`${
                        marker?.address?.length > 35
                          ? `${marker?.address?.slice(0, 35)}...`
                          : marker?.address
                      }`}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text style={{ fontSize: '14px' }}>
                      Số sêri: {marker?.serial_no ?? '-'}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text style={{ fontSize: '14px' }}>
                      Model: {marker?.model ?? '-'}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text style={{ fontSize: '14px' }}>
                      Chỉ số:{' '}
                      {marker?.meter_reading ? formatCurrency(marker?.meter_reading, 3) : '-'}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text style={{ fontSize: '14px' }}>
                      Lần truyền cuối:{' '}
                      {marker?.last_seen_at
                        ? new Intl.DateTimeFormat('vi-VN', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          }).format(marker.last_seen_at)
                        : '-'}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text style={{ fontSize: '14px' }}>
                      Cường độ sóng (dBm): {marker?.rsrp ?? '-'}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text style={{ fontSize: '14px' }}>
                      Tỷ lệ tín/ tạp (dB): {marker?.snr ?? '-'}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text style={{ fontSize: '14px' }}>
                      Dung lượng pin (%): {marker?.battery_capacity ?? '-'}
                    </Typography.Text>
                  </div>
                  <div>
                    <Typography.Text style={{ fontSize: '14px' }}>
                      Cảnh báo:{' '}
                      {_.isEmpty(marker?.alarm_status) ? 'Không có' : marker?.alarm_status}
                    </Typography.Text>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: '8px',
                      width: '100%',
                      justifyContent: 'center'
                    }}>
                    <Button
                      type="primary"
                      icon={<InfoCircleOutlined />}
                      style={{
                        backgroundColor: '#0051A9',
                        color: '#fff',
                        display: 'flex',
                        justifyContent: 'center'
                      }}
                      onClick={() => window.open(`/device/detail/${marker.device_id}`, '_blank')}>
                      Xem chi tiết
                    </Button>
                    <Button
                      type="primary"
                      icon={<RightOutlined />}
                      style={{
                        backgroundColor: '#1A73E8',
                        color: '#fff',
                        display: 'flex',
                        justifyContent: 'center'
                      }}
                      onClick={() => handleDirectionsClick(marker.lat, marker.lng)}>
                      Chỉ đường
                    </Button>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
      </MapContainer>
    </div>
  );
};

export default OpenLayersMap;
